import { type EBIcon } from '@repo/sdk/models/components';

import { NavLink } from '@/components/navigation/nav-link';
import { ActionIcon } from '@/features/equipment-browser/action-components/action-icon';

import { useResolvedDeviceIconConfig } from './use-resolved-device-icon-config';

export type DeviceIconContentProps = {
  deviceId: string;
  fallbackIcon?: string;
  /** When true, render nothing if no active icon state or alarm (e.g. facility map secondary icon). */
  hideWhenInactive?: boolean;
  icon: EBIcon;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** When true, marks the icon as decorative for screen readers (aria-hidden). */
  'aria-hidden'?: boolean;
};

/**
 * Renders device icon visual + alarm after icon ref data is already subscribed.
 * **Pair with** `useSubscribeDeviceIconRefs` in the parent when showing many rows.
 */
export const DeviceIconContent = ({
  deviceId,
  fallbackIcon = 'Settings',
  hideWhenInactive = false,
  icon,
  size = 'md',
  'aria-hidden': ariaHidden,
}: DeviceIconContentProps) => {
  const deviceIconConfig = useResolvedDeviceIconConfig({
    deviceId,
    icon,
    fallbackIcon,
    hideWhenInactive,
  });

  if (!deviceIconConfig) {
    return null;
  }

  const linkId = deviceIconConfig.actions?.find(
    (action) => action.linkAction !== undefined && action.linkAction?.id !== undefined
  )?.linkAction?.id;

  const IconComponent = (
    <ActionIcon
      fallbackIcon={fallbackIcon}
      icon={deviceIconConfig.icon}
      isPulsing={deviceIconConfig.isPulsing}
      size={size}
      tooltipContent={deviceIconConfig.tooltipContent?.value}
      variant={deviceIconConfig.variant}
    />
  );
  const content = linkId ? (
    <NavLink routeId={linkId} onClick={(e) => e.stopPropagation()}>
      {IconComponent}
    </NavLink>
  ) : (
    IconComponent
  );

  if (ariaHidden === true) {
    return <span aria-hidden>{content}</span>;
  }
  return content;
};
